import React, { useState } from 'react';
import { formatDublinDate } from '../../utils/date';
import { useListSentMessagesQuery } from '../../store/api/localApi';
import { Message, MessageTargetType } from '../../types';
import { ScopedLink } from '../../components/ScopedLink';
import { InboxIcon } from '@heroicons/react/24/outline';

// ── SentItemsList ───────────────────────────────────────────────────────────
//
// Renders paginated list of messages sent by the current user. Each row shows
// target, title, recipient count, and read stats. Supports cursor-based
// pagination via a "Load more" button.

type SentMessage = Message & {
  stats: {
    recipientCount: number;
    readCount: number;
    pushFailureCount: number;
    replyCount: number;
  };
};

/** Human-readable description of the message target. */
function targetLabel(msg: SentMessage): string {
  switch (msg.targetType) {
    case MessageTargetType.ORG:
      return 'Entire organisation';
    case MessageTargetType.LOCATION:
      return 'Location';
    case MessageTargetType.CLASS:
      return 'Class';
    case MessageTargetType.STUDENTS:
      return `${msg.stats.recipientCount} student${msg.stats.recipientCount === 1 ? '' : 's'}`;
    default:
      return msg.targetType;
  }
}

function formatDate(iso: string): string {
  return formatDublinDate(iso);
}

const SentItemsList: React.FC = () => {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allMessages, setAllMessages] = useState<SentMessage[]>([]);
  const [nextCursorAfterLoad, setNextCursorAfterLoad] = useState<string | null>(null);

  const { data, isLoading, isFetching, error } = useListSentMessagesQuery(
    { limit: 50, cursor },
    {
      // On each successful fetch, append to allMessages and capture the next cursor.
      selectFromResult: (result) => result,
    }
  );

  // Merge newly fetched page into our accumulated list.
  // We do this in render (not an effect) keyed by the cursor so we don't
  // double-append on re-renders. The accumulated list is the source of truth.
  React.useEffect(() => {
    if (!data) return;
    if (cursor === undefined) {
      // First page — replace
      setAllMessages(data.messages as SentMessage[]);
    } else {
      // Subsequent pages — append, deduplicate by id
      setAllMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const newOnes = (data.messages as SentMessage[]).filter((m) => !existingIds.has(m.id));
        return [...prev, ...newOnes];
      });
    }
    setNextCursorAfterLoad(data.nextCursor);
  }, [data, cursor]);

  const handleLoadMore = () => {
    if (nextCursorAfterLoad) {
      setCursor(nextCursorAfterLoad);
    }
  };

  if (isLoading && allMessages.length === 0) {
    return (
      <div className="py-12 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <p className="mt-2 text-sm text-gray-500">Loading sent messages…</p>
      </div>
    );
  }

  if (error && allMessages.length === 0) {
    return (
      <div className="py-12 text-center">
        <InboxIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading sent messages</h3>
        <p className="mt-1 text-sm text-gray-500">Please try again later.</p>
      </div>
    );
  }

  if (allMessages.length === 0) {
    return (
      <div className="py-16 text-center" data-testid="messages-sent-empty">
        <InboxIcon className="mx-auto h-12 w-12 text-gray-300" />
        <p className="mt-3 text-sm text-gray-500">No sent messages yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="messages-sent-list">
      <div className="bg-white rounded-lg shadow divide-y divide-gray-100 overflow-hidden">
        {allMessages.map((msg) => {
          const readCount = msg.stats.readCount;
          const recipientCount = msg.stats.recipientCount;
          const target = targetLabel(msg);
          const readRatio = `${readCount}/${recipientCount} read`;

          return (
            <ScopedLink
              key={msg.id}
              to={`/messages/${msg.id}`}
              data-testid={`messages-sent-row-${msg.id}`}
              className="block px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{msg.title}</p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2">
                    <p className="text-xs text-gray-500">
                      {target} &bull; {readRatio}
                    </p>
                    {msg.stats.replyCount > 0 && (
                      <span
                        data-testid={`sent-reply-count-${msg.id}`}
                        className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
                      >
                        {msg.stats.replyCount} {msg.stats.replyCount === 1 ? 'family' : 'families'}{' '}
                        replied
                      </span>
                    )}
                  </div>
                </div>
                <time
                  dateTime={msg.createdAt}
                  className="flex-shrink-0 text-xs text-gray-400 whitespace-nowrap"
                >
                  {formatDate(msg.createdAt)}
                </time>
              </div>
            </ScopedLink>
          );
        })}
      </div>

      {nextCursorAfterLoad && (
        <div className="flex justify-center pt-2">
          <button
            type="button"
            data-testid="messages-sent-load-more"
            onClick={handleLoadMore}
            disabled={isFetching}
            className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
          >
            {isFetching ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SentItemsList;
